import React, { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { Form, Field } from "react-final-form";
import { useLazyQuery, useMutation } from "@apollo/client";
import { UserContext } from "../../reuseableComponents/auth/UserContext";
import {
  ADD_IPS_DEFICIENCY_GROUP,
  DELETE_IPS_DEFICIENCY_GROUP,
  GET_IPS_DEFICIENCY_GROUPS,
  GET_IPS_DEFICIENCY_GROUP_BY_GROUP_ID,
  UPDATE_IPS_DEFICIENCY_GROUP
} from "../../../queries/archiveQrys/secureQrys/ipsDeficiencyGroupAdminQrys";
import { ReactSelectAdapter } from "../../reuseableComponents/forms/ReactSelectAdapter";
import Label from "../../reuseableComponents/label/Label";
import ButtonDelete from "../../reuseableComponents/buttons/ButtonDelete";
import ButtonSubmit from "../../reuseableComponents/buttons/ButtonSubmit";
import ButtonProcessing from "../../reuseableComponents/buttons/ButtonProcessing";
import SkeletonLoader from "../../reuseableComponents/loaders/SkeletonLoader";
import { ipsPoints } from "./ipsPoints";

const IpsDeficiencyGroupForm = ({ item, id, handleClose, allDeficiencies, defaultDefValues, setDefaultDefValues }) => {
  const [formSubmitting, setSubmitting] = useState(false);
  const [successMsg, setSuccess] = useState(null);
  const [errorMsg, setError] = useState(null);
  const [grpPtsMsg, setGrpPtsMsg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(UserContext);
  /**
   *
   * @param {Array} groupDeficiencies: The array of deficiencies within a group
   * @param {Array} allDeficiencies - all possible deficiencies that can be assigned to a group
   * This function should return a filtered list to initiate the multiselect
   */
  const handleDeficiencyDefaults = (groupDeficiencies) => {
    let defaultValues = allDeficiencies.filter((element) => {
      return groupDeficiencies.some((elem) => {
        return elem.typeCode === element.value;
      });
    });
    setDefaultDefValues(defaultValues);
  };

  const [addNewGrouptMutation] = useMutation(ADD_IPS_DEFICIENCY_GROUP, {
    refetchQueries: [{ query: GET_IPS_DEFICIENCY_GROUPS }],
    awaitRefetchQueries: true
  });

  const [editGroupMutation] = useMutation(UPDATE_IPS_DEFICIENCY_GROUP, {
    refetchQueries: [{ query: GET_IPS_DEFICIENCY_GROUPS }],
    awaitRefetchQueries: true
  });

  const [deleteGroupMutation] = useMutation(DELETE_IPS_DEFICIENCY_GROUP, {
    refetchQueries: [{ query: GET_IPS_DEFICIENCY_GROUPS }],
    awaitRefetchQueries: true
  });

  const handleDelete = (groupId) => {
    setSubmitting(true);
    deleteGroupMutation({
      variables: { groupId: parseInt(groupId) },
      onCompleted: () => {
        setSuccess("Group added successfully.");
        setTimeout(() => {
          handleClose();
        }, 2000);
      },
      onError: (e) => {
        setError(e);
      }
    });
  };

  const onSubmit = (submit) => {
    console.log(submit);
    //check group points
    if (!submit.groupPoints || !submit.groupPoints.value || !submit.groupPoints.label) {
      setGrpPtsMsg("Required");
      return;
    }
    //check if required deficiencies exist before submitting.
    if (submit.groupDeficiencies.length < 1) {
      setError("One or more deficiencies are required.");
      return;
    }

    setSubmitting(true);
    setIsLoading(true);

    if (id === "new") {
      addNewGrouptMutation({
        variables: {
          groupName: submit.groupName,
          groupDescription: submit.groupDescription,
          groupCreatedBy: user.emailAddress,
          groupCreatedOn: new Date(),
          groupPoints: parseInt(submit.groupPoints.value),
          groupDeficiencies: submit.groupDeficiencies
        },
        onCompleted: () => {
          setSuccess("Group added successfully.");
          setTimeout(() => {
            handleClose();
          }, 2000);
        },
        onError: (e) => {
          setError(e);
        }
      });
    }

    if (id === "edit") {
      setSuccess("Modifying group record. Please wait.");
      editGroupMutation({
        variables: {
          groupId: item.groupId,
          groupName: submit.groupName,
          groupDescription: submit.groupDescription,
          groupCreatedBy: user.emailAddress,
          groupCreatedOn: new Date(),
          groupPoints: parseInt(submit.groupPoints.value),
          groupDeficiencies: submit.groupDeficiencies
        },
        onCompleted: () => {
          setSuccess("Record updated successfully.");
          setSubmitting(false);
          setTimeout(() => {
            handleClose();
          }, 2000);
        },
        onError: (e) => {
          setError(JSON.stringify(e));
        }
      });
    }
  };

  const [getIpsDeficiencyGroupByGroupId] = useLazyQuery(GET_IPS_DEFICIENCY_GROUP_BY_GROUP_ID, {
    variables: { groupId: parseInt(item.groupId) },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      handleDeficiencyDefaults(data.getIpsDeficiencyGroupByGroupId[0].groupDeficiencies);
    },
    onError: (e) => {
      setError(JSON.stringify(e));
    }
  });

  useEffect(() => {
    if (id === "edit" && defaultDefValues.length === 0) {
      getIpsDeficiencyGroupByGroupId(item.groupId);
    }
  }, [defaultDefValues, item.groupId, id, getIpsDeficiencyGroupByGroupId]);

  //a group def has to have at least 1 deficiency assigned to it.
  if (id === "edit" && defaultDefValues.length === 0) {
    return <SkeletonLoader width='100%' />;
  }

  return (
    <Row>
      <Col>
        <Form
          onSubmit={onSubmit}
          keepDirtyOnReinitialize
          initialValues={
            //set default values here and not in the field - setting in field causes useEffect max depth exceeded errors
            item
              ? {
                  groupId: item.groupId,
                  groupName: item.groupName,
                  groupDescription: item.groupDescription,
                  groupPoints: { value: item.groupPoints, label: item.groupPoints },
                  groupDeficiencies: defaultDefValues
                }
              : { groupId: "", groupName: "", groupDescription: "", groupPoints: 0, groupDeficiencies: [] }
          }
          render={({ handleSubmit, form, submitting, values }) => (
            <form onSubmit={handleSubmit}>
              {id !== "delete" ? (
                <>
                  <Row>
                    <Col>
                      <Label htmlFor='label' label='Group Points' required />
                    </Col>
                    <Col>
                      <Field
                        name='groupPoints'
                        component={ReactSelectAdapter}
                        options={ipsPoints}
                        className='mb-2'
                        required
                        disabled={formSubmitting || isLoading}
                      />
                      {grpPtsMsg ? <span className='error'>{grpPtsMsg}</span> : ""}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Label htmlFor='label' label='Group Name' required />
                    </Col>
                    <Col>
                      <Field
                        name='groupName'
                        component='input'
                        type='text'
                        className='form-control mb-2'
                        required={true}
                        disabled={formSubmitting || isLoading}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Label htmlFor='label' label='Group Description' />
                    </Col>
                    <Col>
                      <Field
                        name='groupDescription'
                        component='input'
                        type='text'
                        className='form-control mb-2'
                        disabled={formSubmitting || isLoading}
                      />
                    </Col>
                  </Row>
                  <Row>
                    {!formSubmitting || !isLoading ? (
                      <Col>
                        <Label htmlFor='label' label='Add Group Deficiencies' required />
                        <Field
                          isMulti
                          name='groupDeficiencies'
                          component={ReactSelectAdapter}
                          options={allDeficiencies}
                          className='mb-2'
                          disabled={formSubmitting || isLoading}
                          required
                        />
                      </Col>
                    ) : (
                      <Col>
                        <Label htmlFor='label' label='Add Group Deficiencies' required />
                        <br />
                        {values.groupDeficiencies.map((item) => {
                          return (
                            <>
                              <span key={item.label}>{item.label}</span>
                              <br />
                            </>
                          );
                        })}
                      </Col>
                    )}
                  </Row>
                  <Row>
                    <Col>
                      <span className='info'>
                        <strong>{successMsg}</strong>
                      </span>
                      <span className='error'>{errorMsg}</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {formSubmitting || isLoading ? (
                        <ButtonProcessing id='ddw-primary' style={{ float: "right" }} disabled />
                      ) : (
                        <ButtonSubmit id='ddw-primary' style={{ float: "right" }} disabled={isLoading} />
                      )}
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row>
                    <Col>
                      <span>
                        Are you sure you want to delete the IPS Deficiency Group <b>{item.groupName}</b>?
                      </span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {id === "delete" && !formSubmitting ? (
                        <ButtonDelete
                          style={{ float: "right" }}
                          onClick={() => {
                            handleDelete(item.groupId);
                          }}
                        />
                      ) : (
                        <ButtonDelete style={{ float: "right" }} label='Deleting ...' disabled />
                      )}
                    </Col>
                  </Row>
                </>
              )}
            </form>
          )}
        />
      </Col>
    </Row>
  );
};

export default IpsDeficiencyGroupForm;
